// src/utils/formatters.js

/**
 * Formatear número como moneda EUR
 * @param {Number} value - Valor numérico
 * @returns {String} - Formato: "1.234.567 €"
 */
export const formatCurrency = (value) => {
  if (value === null || value === undefined) return '0 €';
  
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

/**
 * Formatear número con separadores de miles
 * @param {Number} value - Valor numérico
 * @returns {String} - Formato: "1.234.567"
 */
export const formatNumber = (value) => {
  if (value === null || value === undefined) return '0';
  
  return new Intl.NumberFormat('es-ES').format(value);
};

/**
 * Formatear porcentaje
 * @param {Number} value - Valor numérico (0-100)
 * @param {Number} decimals - Número de decimales (default: 1)
 * @returns {String} - Formato: "24,5%"
 */
export const formatPercentage = (value, decimals = 1) => {
  if (value === null || value === undefined) return '0%';
  
  return new Intl.NumberFormat('es-ES', {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value / 100);
};

/**
 * Formatear fecha
 * @param {String|Date} date - Fecha a formatear
 * @param {String} format - Formato deseado ('short', 'medium', 'long')
 * @returns {String} - Fecha formateada
 */
export const formatDate = (date, format = 'short') => {
  if (!date) return '';
  
  const dateObj = new Date(date);
  
  const formats = {
    short: { day: '2-digit', month: '2-digit', year: 'numeric' }, // 01/12/2024
    medium: { day: '2-digit', month: 'short', year: 'numeric' },  // 01 dic 2024
    long: { day: '2-digit', month: 'long', year: 'numeric' },     // 01 de diciembre de 2024
  };
  
  return new Intl.DateTimeFormat('es-ES', formats[format]).format(dateObj);
};

/**
 * Abreviar números grandes (K, M, B)
 * @param {Number} value - Valor numérico
 * @returns {String} - Formato: "1.2M", "345K"
 */
export const formatCompactNumber = (value) => {
  if (value === null || value === undefined) return '0';
  
  return new Intl.NumberFormat('es-ES', {
    notation: 'compact',
    compactDisplay: 'short',
  }).format(value);
};

/**
 * Calcular variación porcentual entre dos valores
 * @param {Number} current - Valor actual
 * @param {Number} previous - Valor anterior
 * @returns {Object} - { value, isPositive }
 */
export const calculateVariation = (current, previous) => {
  if (!previous || previous === 0) {
    return { value: 0, isPositive: true };
  }
  
  const variation = ((current - previous) / previous) * 100;
  
  return {
    value: Math.abs(variation),
    isPositive: variation >= 0,
  };
};

/**
 * Obtener color según valor positivo/negativo
 * @param {Boolean} isPositive - Es positivo?
 * @returns {String} - Color en formato hex
 */
export const getVariationColor = (isPositive) => {
  return isPositive ? '#10b981' : '#ef4444'; // verde / rojo
};